import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, Truck } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import { CatalogTable } from '@/features/catalog/Catalog';
import type { DeliveryMethodBody } from '@/shared/api/generated/model';
import {
  shopDeliveryCreate,
  shopDeliveryDelete,
  shopDeliveryList,
  shopDeliveryReorder,
  shopDeliveryUpdate,
} from '@/shared/api/generated/shop/shop';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog, Dialog, DialogContent } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Segmented } from '@/shared/ui/Segmented';
import { Switch } from '@/shared/ui/Switch';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import styles from './ShopPage.module.scss';
import { DELIVERY_KIND, deliveryPrice, linesOf } from './shop';

type Kind = DeliveryMethodBody['kind'];

/**
 * Способы доставки (шаг 9.8): свои у окружения. В этом порядке их видит
 * покупатель. Разные цены по городам — разные способы.
 */
export function DeliveryTab({ siteId, env }: { siteId: string; env: EnvKey }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<DeliveryMethodBody | 'new'>();
  const [removing, setRemoving] = useState<DeliveryMethodBody>();
  const key = ['shop-delivery', siteId, env];

  const methods = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await shopDeliveryList(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список способов доставки');
      }

      return response.data.items ?? [];
    },
  });

  const items = methods.data ?? [];
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: key });
  };

  const move = (index: number, delta: number) => {
    const next = [...items];
    const [moved] = next.splice(index, 1);

    if (moved === undefined) {
      return;
    }

    next.splice(index + delta, 0, moved);
    queryClient.setQueryData(key, next);

    shopDeliveryReorder(siteId, kinds[env], { ids: next.map((m) => m.id) })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось поменять порядок');
      })
      .finally(refresh);
  };

  return (
    <div className={styles['list']}>
      {methods.isPending ? null : items.length === 0 ? (
        <EmptyState
          icon={<Truck />}
          title="Способов доставки нет"
          description="Пока их нет, заказ оформляется без доставки. Заведите курьера, пункт выдачи или самовывоз — и покупатель выберет один из них, а цена войдёт в сумму заказа."
          action={
            <Button
              onClick={() => {
                setEditing('new');
              }}
            >
              Завести способ
            </Button>
          }
        />
      ) : (
        <>
          <div>
            <Button
              iconLeft={<Plus size={15} />}
              onClick={() => {
                setEditing('new');
              }}
            >
              Новый способ
            </Button>
          </div>
          <CatalogTable head={['Способ', 'Цена', 'Где', 'Срок', '']}>
            {items.map((m, index) => (
              <tr key={m.id}>
                <td>
                  {m.name}
                  <div className={styles['muted']}>{DELIVERY_KIND[m.kind]}</div>
                </td>
                <td>{deliveryPrice(m)}</td>
                <td>
                  {m.kind === 'store'
                    ? (m.points ?? []).join('; ')
                    : (m.cities ?? []).length === 0
                      ? 'везде'
                      : (m.cities ?? []).join(', ')}
                </td>
                <td>{m.eta || '—'}</td>
                <td>
                  <div className={styles['panel__actions']}>
                    {!m.enabled && (
                      <Badge size="sm" tone="neutral">
                        выключен
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label="Выше"
                      disabled={index === 0}
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      <ArrowUp size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label="Ниже"
                      disabled={index === items.length - 1}
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      <ArrowDown size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label="Изменить"
                      onClick={() => {
                        setEditing(m);
                      }}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label="Удалить"
                      onClick={() => {
                        setRemoving(m);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </CatalogTable>
        </>
      )}

      {editing !== undefined && (
        <MethodDialog
          siteId={siteId}
          env={env}
          method={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
          onSaved={() => {
            setEditing(undefined);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(undefined);
          }
        }}
        title={`Удалить «${removing?.name ?? ''}»?`}
        description="Покупатели больше не смогут его выбрать. В уже оформленных заказах способ записан и останется."
        confirmLabel="Удалить"
        tone="danger"
        onConfirm={async () => {
          if (removing === undefined) {
            return;
          }

          try {
            await shopDeliveryDelete(removing.id);
            setRemoving(undefined);
            refresh();
          } catch (cause) {
            toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось удалить');
          }
        }}
      />
    </div>
  );
}

function MethodDialog({
  siteId,
  env,
  method,
  onClose,
  onSaved,
}: {
  siteId: string;
  env: EnvKey;
  method: DeliveryMethodBody | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(method?.name ?? '');
  const [kind, setKind] = useState<Kind>(method?.kind ?? 'courier');
  const [price, setPrice] = useState(method === undefined ? '' : String(method.price));
  const [freeFrom, setFreeFrom] = useState(
    method?.free_from === undefined ? '' : String(method.free_from),
  );
  const [eta, setEta] = useState(method?.eta ?? '');
  const [cities, setCities] = useState((method?.cities ?? []).join(', '));
  const [points, setPoints] = useState((method?.points ?? []).join('\n'));
  const [enabled, setEnabled] = useState(method?.enabled ?? true);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();

    const cost = price.trim() === '' ? 0 : Number(price.replace(',', '.'));
    const free = freeFrom.trim() === '' ? undefined : Number(freeFrom.replace(',', '.'));

    if (!(cost >= 0) || (free !== undefined && !(free >= 0))) {
      setError('Цена и порог — числа от 0');

      return;
    }

    const body = {
      name: name.trim(),
      kind,
      price: cost,
      ...(free === undefined ? {} : { free_from: free }),
      eta: eta.trim(),
      cities: kind === 'store' ? [] : linesOf(cities, ','),
      points: kind === 'store' ? linesOf(points, '\n') : [],
      enabled,
    };

    setBusy(true);
    setError(undefined);

    (method === undefined
      ? shopDeliveryCreate(siteId, kinds[env], body)
      : shopDeliveryUpdate(method.id, body)
    )
      .then(() => {
        toast.success(method === undefined ? 'Способ доставки заведён' : 'Сохранено');
        onSaved();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={method === undefined ? 'Новый способ доставки' : method.name}
        size="md"
        dismissible={false}
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" form="delivery-form" loading={busy}>
              Сохранить
            </Button>
          </>
        }
      >
        <form id="delivery-form" className={styles['form']} onSubmit={submit} noValidate>
          <Field label="Вид">
            <Segmented
              value={kind}
              aria-label="Вид доставки"
              onValueChange={(next) => {
                setKind(next as Kind);
              }}
              options={[
                { value: 'courier', label: DELIVERY_KIND.courier },
                { value: 'pickup_point', label: DELIVERY_KIND.pickup_point },
                { value: 'store', label: DELIVERY_KIND.store },
              ]}
            />
          </Field>

          <Field label="Название" hint="Его видит покупатель: «Курьер по Москве»">
            <Input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
          </Field>

          <div className={styles['row']}>
            <Field label="Цена, ₽">
              <Input
                inputMode="decimal"
                placeholder="0 — бесплатно"
                value={price}
                onChange={(event) => {
                  setPrice(event.target.value);
                }}
              />
            </Field>
            <Field label="Бесплатно от, ₽" hint="Сумма товаров со скидкой">
              <Input
                inputMode="decimal"
                placeholder="всегда по цене"
                value={freeFrom}
                onChange={(event) => {
                  setFreeFrom(event.target.value);
                }}
              />
            </Field>
          </div>

          <Field label="Срок" hint="Словами: «2–3 дня», «сегодня»">
            <Input
              value={eta}
              onChange={(event) => {
                setEta(event.target.value);
              }}
            />
          </Field>

          {kind === 'store' ? (
            <Field label="Откуда забрать" hint="По адресу на строку — покупатель выберет один">
              <Textarea
                rows={3}
                value={points}
                onChange={(event) => {
                  setPoints(event.target.value);
                }}
              />
            </Field>
          ) : (
            <Field
              label="Только для городов"
              hint="Через запятую; пусто — везде. Разные цены по городам — разные способы"
            >
              <Input
                placeholder="Москва, Санкт-Петербург"
                value={cities}
                onChange={(event) => {
                  setCities(event.target.value);
                }}
              />
            </Field>
          )}

          <Switch checked={enabled} onCheckedChange={setEnabled}>
            Предлагать покупателю
          </Switch>

          {error !== undefined && <p className={styles['error']}>{error}</p>}
        </form>
      </DialogContent>
    </Dialog>
  );
}
